import CampaignIcon from "@mui/icons-material/Campaign";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useChat } from "../../../context";
import { ContactInfoSidebarProps } from "../../../interfaces/all";
import Header from "../Header/Header";

const SidebarContainer = styled(Box)({
  width: "100%",
  height: "100%",
  backgroundColor: "#ffffff",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
});

const CloseButton = styled(IconButton)({
  padding: "4px",
  color: "#54656f",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const Title = styled(Typography)({
  fontSize: "16px",
  fontWeight: 500,
  color: "#111b21",
});

const ProfileSection = styled(Box)({
  padding: "24px 16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  backgroundColor: "#f0f2f5",
});

const StyledAvatar = styled(Avatar)({
  width: "80px",
  height: "80px",
  backgroundColor: "#e0f2f1",
  color: "#008069",
  fontSize: "40px",
  border: "3px solid #008069",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
});

const ContactName = styled(Typography)({
  fontSize: "18px",
  fontWeight: 500,
  color: "#111b21",
  textAlign: "center",
});

const InfoSection = styled(Box)({
  padding: "16px",
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#f5f6f6",
  },
});

const InfoIconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  backgroundColor: "#e9edef",
  color: "#54656f",
});

const InfoContent = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const InfoLabel = styled(Typography)({
  fontSize: "13px",
  color: "#667781",
  marginBottom: "4px",
});

const InfoValue = styled(Typography)({
  fontSize: "15px",
  color: "#111b21",
  wordBreak: "break-word",
  "&:empty::before": {
    content: '"Não informado"',
    color: "#667781",
    fontStyle: "italic",
  },
});

const SectionDivider = styled(Divider)({
  margin: "0 16px",
  backgroundColor: "#e9edef",
});

const ContactInfoSidebar: React.FC<ContactInfoSidebarProps> = ({
  isOpen,
  onClose,
}) => {
  const { selectedConversation } = useChat();
  const [loading, setLoading] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 800);

    // Cleanup para evitar race condition se componente for desmontado
    return () => {
      clearTimeout(timer);
      setShowSkeleton(false);
    };
  }, []);

  if (!isOpen || !selectedConversation) return null;

  if (showSkeleton) {
    return (
      <SidebarContainer>
        <Header title="Informações do contato">
          <CloseButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </CloseButton>
        </Header>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Skeleton variant="circular" width={60} height={60} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={16} />
            </Box>
          </Box>
          {Array.from({ length: 4 }).map((_, index) => (
            <Box key={`contact-info-skeleton-${index}`} sx={{ mb: 2 }}>
              <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={20} />
            </Box>
          ))}
        </Box>
      </SidebarContainer>
    );
  }

  const contactName = (
    selectedConversation.valid_name || selectedConversation.name
  )
    ?.split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
  const contactPhone =
    selectedConversation.valid_telephone || selectedConversation.telephone;
  const contactEmail = selectedConversation.email;
  const campaignName = selectedConversation.campaign_name;
  const attendantName = selectedConversation.attendant_name;
  const coordinatorName = selectedConversation.coordinator_name;

  return (
    <SidebarContainer>
      <Header title="Informações do contato">
        <CloseButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 20 }} />
        </CloseButton>
      </Header>

      <ProfileSection>
        <StyledAvatar>
          <PersonIcon sx={{ fontSize: 40 }} />
        </StyledAvatar>
        <ContactName>{contactName}</ContactName>
      </ProfileSection>

      <SectionDivider />

      <InfoSection>
        <InfoIconWrapper>
          <PhoneIcon sx={{ fontSize: 18 }} />
        </InfoIconWrapper>
        <InfoContent>
          <InfoLabel>Telefone</InfoLabel>
          <InfoValue>{contactPhone}</InfoValue>
        </InfoContent>
      </InfoSection>

      <InfoSection>
        <InfoIconWrapper>
          <EmailIcon sx={{ fontSize: 18 }} />
        </InfoIconWrapper>
        <InfoContent>
          <InfoLabel>Email</InfoLabel>
          <InfoValue>{contactEmail}</InfoValue>
        </InfoContent>
      </InfoSection>

      <InfoSection>
        <InfoIconWrapper>
          <CampaignIcon sx={{ fontSize: 18 }} />
        </InfoIconWrapper>
        <InfoContent>
          <InfoLabel>Campanha</InfoLabel>
          <InfoValue>{campaignName}</InfoValue>
        </InfoContent>
      </InfoSection>

      {attendantName && (
        <InfoSection>
          <InfoIconWrapper>
            <PersonIcon sx={{ fontSize: 18 }} />
          </InfoIconWrapper>
          <InfoContent>
            <InfoLabel>Atendente</InfoLabel>
            <InfoValue>{attendantName}</InfoValue>
          </InfoContent>
        </InfoSection>
      )}

      {coordinatorName && (
        <InfoSection>
          <InfoIconWrapper>
            <SupervisorAccountIcon sx={{ fontSize: 18 }} />
          </InfoIconWrapper>
          <InfoContent>
            <InfoLabel>Coordenador</InfoLabel>
            <InfoValue>{coordinatorName}</InfoValue>
          </InfoContent>
        </InfoSection>
      )}
    </SidebarContainer>
  );
};

export default ContactInfoSidebar;
